import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as Types from 'types'
import * as apiActions from 'api-actions'
import * as flashActions from 'redux-flash'
import { selectors as globalSelectors } from '../global-reducer'

export function CourseBookmarkIcon({ active }) {
  return active ? (
    <svg width="18" height="22" viewBox="0 0 14 18" aria-hidden>
      <path
        d="M2 .75c-.342 0-.65.14-.874.365C.9 1.342.76 1.655.76 2L.75 16.862 7 14.184l6.25 2.679V2c0-.299 0-.591-.3-.885-.3-.294-.528-.365-.933-.365H2z"
        fill="#FFFFFF"
        stroke="#FFFFFF"
        strokeWidth="1.5"
      />
    </svg>
  ) : (
    <svg width="18" height="22" viewBox="0 0 14 18" aria-hidden>
      <path
        d="M2 .75c-.342 0-.65.14-.874.365C.9 1.342.76 1.655.76 2L.75 16.862 7 14.184l6.25 2.679V2c0-.299 0-.591-.3-.885-.3-.294-.528-.365-.933-.365H2z"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="1.5"
      />
    </svg>
  )
}

CourseBookmarkIcon.propTypes = {
  active: PropTypes.bool.isRequired,
}

function LearningHubCourseBookmarkButton({
  courseId,
  courseTitle,
  bookmarks,
  addLearningHubBookmark,
  removeLearningHubBookmark,
  fetchLearningHubMyCourses,
  flashSuccessMessage,
  flashErrorMessage,
  className,
}) {
  const bookmark = useMemo(
    () =>
      (bookmarks || []).find(
        (b) =>
          Number(b.bookmarkableId) === Number(courseId) &&
          b.bookmarkableType === Types.BOOKMARKABLE.LEARNING_HUB_COURSE
      ),
    [bookmarks, courseId]
  )

  const isBookmarked = Boolean(bookmark)

  const toggleBookmark = useCallback(() => {
    if (isBookmarked) {
      removeLearningHubBookmark({
        bookmarkId: bookmark.id,
        bookmarkableType: Types.BOOKMARKABLE.LEARNING_HUB_COURSE,
      })
        .then(({ successMessage }) => {
          flashSuccessMessage(successMessage)
          return fetchLearningHubMyCourses()
        })
        .catch(({ errorMessage }) => {
          flashErrorMessage(errorMessage)
        })
      return
    }

    addLearningHubBookmark({
      bookmarkableId: Number(courseId),
      bookmarkableType: Types.BOOKMARKABLE.LEARNING_HUB_COURSE,
    })
      .then(({ successMessage }) => {
        flashSuccessMessage(successMessage)
        return fetchLearningHubMyCourses()
      })
      .catch(({ errorMessage }) => {
        flashErrorMessage(errorMessage)
      })
  }, [
    isBookmarked,
    bookmark,
    courseId,
    courseTitle,
    addLearningHubBookmark,
    removeLearningHubBookmark,
    fetchLearningHubMyCourses,
    flashSuccessMessage,
    flashErrorMessage,
  ])

  return (
    <button
      type="button"
      className={className}
      aria-label={`Bookmark ${courseTitle || 'course'}`}
      aria-pressed={isBookmarked}
      onClick={toggleBookmark}
    >
      <CourseBookmarkIcon active={isBookmarked} />
    </button>
  )
}

LearningHubCourseBookmarkButton.propTypes = {
  courseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  courseTitle: PropTypes.string,
  bookmarks: PropTypes.arrayOf(Types.bookmark).isRequired,
  addLearningHubBookmark: PropTypes.func.isRequired,
  removeLearningHubBookmark: PropTypes.func.isRequired,
  fetchLearningHubMyCourses: PropTypes.func.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  className: PropTypes.string,
}

LearningHubCourseBookmarkButton.defaultProps = {
  courseTitle: '',
  className: 'learning-hub-course-card__bookmark-button',
}

export default compose(
  connect(
    (state) => ({
      bookmarks: globalSelectors.bookmarks(state),
    }),
    {
      addLearningHubBookmark: apiActions.addLearningHubBookmark,
      removeLearningHubBookmark: apiActions.removeLearningHubBookmark,
      fetchLearningHubMyCourses: apiActions.fetchLearningHubMyCourses,
      flashSuccessMessage: flashActions.flashSuccessMessage,
      flashErrorMessage: flashActions.flashErrorMessage,
    }
  )
)(LearningHubCourseBookmarkButton)
